import { ConcurrencySweepChart } from '@/components/charts';
import type { Block, DocSection } from '@/components/investor';

/**
 * Fnk investor one-pager. Mirrors docs/investors/fnk/one-pager.md; every figure
 * comes from docs/business-plan/fnk-business-plan.md — keep the three in sync.
 */

export const onePagerStatus = 'TRL 6 · Pre-revenue · FNK Labs · Spain / EU · No external funding through development · Seed modelled at €1.2M–€2.0M after a first paying customer';

export const onePagerSummary =
  '**Fnk is the AI stack, the Harness and the inference underneath enterprise AI.** An agent framework, a control layer and a reactive inference engine built as one non-blocking system, plus the configured layer — workflows, tools, policies, guardrails and evaluation sets — that turns it into a governed service. Licensed per GPU on a buyer’s own hardware, or delivered as a complete box where nothing leaves the customer’s environment.';

export const onePagerTop: Block[] = [
  {
    kind: 'metrics',
    items: [
      { value: '2.0×', label: 'Completed requests per second over Spring MVC + LangChain4j + vLLM, same NVIDIA GPU' },
      { value: '1.4× → 2.0×', label: 'Lead at every tested concurrency' },
      { value: '≈87%', label: 'Gross margin in every recurring year of a box' },
      { value: '€0', label: 'External funding required in any scenario', tone: 'plain' },
    ],
  },
];

export const onePagerSections: DocSection[] = [
  {
    title: 'Vision, mission and positioning',
    blocks: [
      {
        kind: 'text',
        text: '**Vision:** LLMs are replacing buttons and forms as the interface to services, and behind every conversation runs an agentic workload that automates the service and makes it intelligent — Fnk builds the tools and the product that workload runs on. **Mission:** turn open-weight models into a governed, private and efficient AI service on hardware the customer controls — and prove every claim on the customer’s own workload. **Positioning:** enterprise AI infrastructure, *built as one system* — *more completed work from every GPU* for operators who run a fleet, *private AI without a platform team* for regulated enterprises.',
      },
    ],
  },
  {
    title: 'Problem',
    blocks: [
      {
        kind: 'text',
        text: 'Enterprises want private AI over their own data and cannot assemble it: inference, orchestration, retrieval, identity, connectors, observability, governance and operations are nine disciplines, and a mid-market enterprise has one or two. Operators who do run GPUs pay for accelerators that wait on software: the standard stack bolts a LangChain-class framework onto vLLM across an HTTP boundary that carries **neither capacity upward nor cancellation downward**, so saturated runtimes keep admitting work and abandoned requests keep generating tokens.',
      },
    ],
  },
  {
    title: 'Product — the stack, the Harness, and how to buy them',
    blocks: [
      {
        kind: 'matrix',
        head: ['Form', 'Price', 'Buyer'],
        rows: [
          ['SaaS subscription', 'Monthly or annual', 'Teams that want the stack without running GPUs'],
          ['Stack licence', 'Per GPU per year, one list price per hardware class', 'Inference providers, data centres, enterprise GPU estates, OEMs'],
          ['Partial box', '≈€200k year one · €145k recurring', 'Enterprises with a data centre'],
          ['Full box', '≈€290k year one · €150k recurring', 'Regulated or air-gapped organisations without a platform team'],
        ],
      },
      {
        kind: 'text',
        text: '**The Harness** — vertical workflow packs, tool and action schemas, policy packs, confirmation gates, grounding configuration and evaluation sets — is provider-neutral and ships inside every box. **DialogMe** ([dialogme.ai](https://dialogme.ai)), a conversational AI platform, is Fnk’s first customer and runs its production workload on the stack.',
      },
    ],
  },
  {
    title: 'Where it stands — TRL 6, gate passed',
    blocks: [
      {
        kind: 'matrix',
        head: ['At maximum tested concurrency (64)', 'Fnk AI stack vs assembled stack'],
        rows: [
          ['Completed requests per second', '**2.0× more**'],
          ['Weighted mean latency', '**1.6× lower**'],
          ['Stable-stage p95 latency', '**2.4× lower**'],
          ['Inference output-token throughput', '**1.4× higher**'],
        ],
        numeric: [1],
        minWidth: '360px',
      },
      { kind: 'node', node: <ConcurrencySweepChart /> },
      {
        kind: 'text',
        text: 'Paired run: identical workload, same model, same hardware, concurrency swept 4 → 64; the gate required ≥1.2×. **A reactive backend still talking to vLLM over HTTP barely helped** — the gain appears only when capacity, admission and cancellation cross the inference boundary. Corroborated on Apple MLX (1.8× throughput, 2.8× faster time-to-first-token vs LM Studio).',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Caveats, kept attached',
        text: 'Whole-stack result, not an engine benchmark; one run per point; engineering evidence, not an SLA; **not yet independently reproduced**.',
      },
    ],
  },
  {
    title: 'Why now',
    blocks: [
      {
        kind: 'text',
        text: 'Public cloud as the primary home for production inference fell **56% → 41%**; ≈56% of enterprises now run or plan private inference; EU AI Act transparency obligations now apply. NVIDIA AI Enterprise lists at **$4,500 per GPU per year** — the buyer already budgets software per accelerator, and Fnk prices beneath it on every hardware class. OEMs earn ≈5% on AI servers and need software differentiation.',
      },
    ],
  },
  {
    title: 'Competition',
    blocks: [
      {
        kind: 'text',
        text: 'Private AI infrastructure (HPE, Dell, Nutanix) delivers a platform to build on. Sovereign platforms (Cohere + Aleph Alpha, watsonx, Mistral) sell above this band. Cloud knowledge AI (Glean, Copilot) cannot keep data inside. Open source (vLLM, LangChain, Onyx) is free and needs a permanent team. Inference software (NVIDIA AI Enterprise, Red Hat, Baseten) sells an engine without the framework above it. **Nobody sells the framework and the engine as one system with a capacity contract between them.**',
      },
    ],
  },
  {
    title: 'Market and financials',
    blocks: [
      {
        kind: 'matrix',
        head: ['Line', 'Addressable (EU)', 'Y5 base target', 'Share'],
        rows: [
          ['Partial box', '≈4,300', '6 customers', '0.14%'],
          ['Full box', '≈1,800', '3 customers', '0.17%'],
          ['Stack licence', '≈2,400', 'Not in base case', 'Upside'],
        ],
        numeric: [1],
        minWidth: '420px',
      },
      {
        kind: 'matrix',
        head: ['Scenario', 'Development', 'First revenue', 'Year-5 revenue', 'EBITDA-positive', 'Coverage'],
        rows: [
          ['Pessimistic', '24 months', 'Year 3 — €15,000', '€665,500', 'Year 4', '1.64×'],
          ['Base', '12 months', 'Year 2 — €20,000', '€1,786,500', 'Year 3', '1.24×'],
          ['Optimistic', '6 months', 'Year 1 — €10,000', '€4,264,500', 'Year 2', '1.83×'],
        ],
        accentRows: [1],
        numeric: [3, 5],
        minWidth: '640px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        text: 'Development costs €22,076 in year one and €17,976 a year thereafter — **€0 of external funding in any scenario**. The eighteen months between best and worst are worth **€3,599,000** of Year-5 revenue. Box prices 30% lower still reach Year 5 profitable but take cash below zero on the way. These are management scenarios, not contracted revenue.',
      },
    ],
  },
  {
    title: 'Strategy and go-to-market',
    blocks: [
      {
        kind: 'text',
        text: '**Five choices:** own every layer under the application; sell in the unit the buyer already budgets (per GPU); references before enterprise; let the buyer measure; Europe first, built from Spain.',
      },
      {
        kind: 'text',
        text: '**References first** — DialogMe, the first customer, in production on the stack, and paid efficiency assessments on a buyer’s own workload; **then enterprise**, worked through events with pre-booked meetings and paid assessments (€12,000–€35,000) as the entry point; **then partners**, once a second deployment in a vertical costs under 60% of the first. Cold contact to signature runs 3–12 months, which is why the box follows revenue rather than leading it.',
      },
    ],
  },
  {
    title: 'The ask — not yet, and when',
    blocks: [
      {
        kind: 'text',
        text: '**Not raising during development.** The seed conversation opens after independent reproduction of the benchmark and a first paying customer:',
      },
      {
        kind: 'metrics',
        items: [
          { value: '€1.2–2.0M', label: 'Seed modelled, base column' },
          { value: '€5–8M', label: 'Pre-money valuation', tone: 'plain' },
          { value: '15–22%', label: 'Implied dilution', tone: 'plain' },
          { value: '18–30 mo', label: 'Runway at the resulting cost base', tone: 'plain' },
        ],
      },
      {
        kind: 'text',
        text: 'Engineering 45% · delivery capacity 17.5% · go-to-market 15% · benchmark reproduction and certification 7.5% · working capital 10% · reserve 5%.',
      },
      {
        kind: 'callout',
        title: 'Where it goes',
        text: 'The modelled exit is €8m–€16m. Beyond it: **the infrastructure standard** — the framework and engine data centres standardise on and OEMs ship preinstalled. First evidence, zero reference deployments, and in no figure above.',
      },
    ],
  },
];
